import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { api } from '../lib/api';
import Icon from '../components/Icon';

interface Scenario {
  id: string;
  name: string;
  description: string;
  icon: string;
  durationSeconds: number;
  botCount: number;
}

interface BotStatus {
  activeAuctions: number;
  bots: Record<string, Array<{ id: string; name: string; strategy: string; active: boolean }>>;
}

export default function DemoPage() {
  const navigate = useNavigate();
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [selectedScenario, setSelectedScenario] = useState<string>('bidding-war');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [botStatus, setBotStatus] = useState<BotStatus | null>(null);

  // Fetch available scenarios
  useEffect(() => {
    const fetchScenarios = async () => {
      try {
        const response = await api.get<{ success: boolean; data: Scenario[] }>('/api/demo/scenarios');
        setScenarios(response.data);
      } catch (err) {
        console.error('Failed to fetch scenarios:', err);
        // Fallback scenarios if API not available
        setScenarios([
          { id: 'bidding-war', name: 'Bidding War', description: 'Three aggressive bots compete fiercely', icon: 'local_fire_department', durationSeconds: 90, botCount: 3 },
          { id: 'the-sniper', name: 'The Sniper', description: 'Watch a sniper bot swoop in last second', icon: 'schedule', durationSeconds: 60, botCount: 3 },
          { id: 'anti-sniping', name: 'Anti-Sniping Demo', description: 'Late bid triggers auction extension', icon: 'timer', durationSeconds: 45, botCount: 2 },
          { id: 'reserve-not-met', name: 'Reserve Not Met', description: 'Bids come but reserve is not reached', icon: 'block', durationSeconds: 45, botCount: 2 },
        ]);
      }
    };
    fetchScenarios();
  }, []);

  // Fetch bot status
  useEffect(() => {
    const fetchBotStatus = async () => {
      try {
        const response = await api.get<{ success: boolean; data: BotStatus }>('/api/demo/bots/status');
        setBotStatus(response.data);
      } catch (err) {
        console.error('Failed to fetch bot status:', err);
      }
    };
    fetchBotStatus();
    const interval = setInterval(fetchBotStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  // Run selected demo
  const runDemo = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post<{ 
        success: boolean; 
        data: { auctionId: string; redirectUrl: string } 
      }>('/api/demo/run', { scenarioId: selectedScenario });

      // Redirect to auction page
      navigate(response.data.redirectUrl);
    } catch (err) {
      setError((err as Error).message);
      setLoading(false);
    }
  };

  // Stop all bots
  const stopAllBots = async () => {
    try {
      await api.post('/api/demo/bots/stop', {});
      setBotStatus({ activeAuctions: 0, bots: {} });
    } catch (err) {
      console.error('Failed to stop bots:', err);
    }
  };

  const selectedScenarioData = scenarios.find(s => s.id === selectedScenario);

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Icon name="smart_toy" size="large" className="text-primary-600" />
            Demo Control Center
          </h1>
          <p className="text-gray-500 mt-1">
            One-click automated demos showcasing the auction platform
          </p>
        </div>
      </div>

      {/* Active Bots Warning */}
      {botStatus && botStatus.activeAuctions > 0 && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon name="smart_toy" className="text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">
                Demo in Progress
              </p>
              <p className="text-sm text-amber-600">
                {botStatus.activeAuctions} auction(s) with active bots
              </p>
            </div>
          </div>
          <button
            onClick={stopAllBots}
            className="px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors flex items-center gap-2"
          >
            <Icon name="stop" size="small" />
            Stop All
          </button>
        </div>
      )}

      {/* Scenario Selection */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Icon name="movie" size="small" className="text-gray-600" />
          Choose a Scenario
        </h2>
        
        <div className="grid md:grid-cols-2 gap-4">
          {scenarios.map((scenario) => (
            <button
              key={scenario.id}
              onClick={() => setSelectedScenario(scenario.id)}
              className={clsx(
                'p-4 rounded-xl border-2 text-left transition-all',
                selectedScenario === scenario.id
                  ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-200'
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-md'
              )}
            >
              <div className="flex items-start gap-3">
                <div className={clsx(
                  'p-2 rounded-lg',
                  selectedScenario === scenario.id ? 'bg-primary-100' : 'bg-gray-100'
                )}>
                  <Icon 
                    name={scenario.icon} 
                    className={selectedScenario === scenario.id ? 'text-primary-600' : 'text-gray-600'} 
                  />
                </div>
                <div className="flex-1">
                  <h3 className={clsx(
                    'font-semibold',
                    selectedScenario === scenario.id ? 'text-primary-900' : 'text-gray-900'
                  )}>
                    {scenario.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {scenario.description}
                  </p>
                  <div className="flex items-center gap-4 mt-3 text-xs text-gray-400">
                    <span className="flex items-center gap-1">
                      <Icon name="timer" size="small" />
                      {scenario.durationSeconds}s
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="smart_toy" size="small" />
                      {scenario.botCount} bots
                    </span>
                  </div>
                </div>
                {selectedScenario === scenario.id && (
                  <Icon name="check_circle" className="text-primary-600" />
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Run Demo Button */}
      <div className="bg-gradient-to-br from-primary-50 to-white rounded-xl border border-primary-100 p-6 mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-gray-900">
              {selectedScenarioData?.name || 'Select a Scenario'}
            </h3>
            <p className="text-gray-500 mt-1">
              {selectedScenarioData?.description}
            </p>
            <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Icon name="timer" size="small" />
                Duration: {selectedScenarioData?.durationSeconds || 0} seconds
              </span>
              <span className="flex items-center gap-1">
                <Icon name="smart_toy" size="small" />
                Bots: {selectedScenarioData?.botCount || 0}
              </span>
            </div>
          </div>
          <button
            onClick={runDemo}
            disabled={loading || !selectedScenario}
            className={clsx(
              'px-8 py-4 rounded-xl font-semibold text-lg transition-all flex items-center gap-3',
              loading
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-primary-600 text-white hover:bg-primary-700 shadow-lg hover:shadow-xl'
            )}
          >
            {loading ? (
              <>
                <Icon name="hourglass_empty" className="animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Icon name="play_arrow" />
                Run Demo
              </>
            )}
          </button>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <Icon name="error" size="small" />
            {error}
          </div>
        )}
      </div>

      {/* What Happens Section */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Icon name="info" size="small" className="text-blue-600" />
          What Happens When You Click "Run Demo"
        </h3>
        <ol className="space-y-3">
          {[
            { icon: 'refresh', text: 'Resets demo data and funds all bot wallets' },
            { icon: 'gavel', text: 'Creates a new auction with selected scenario settings' },
            { icon: 'smart_toy', text: 'Activates AI bots with different bidding strategies' },
            { icon: 'open_in_new', text: 'Redirects you to watch the live auction' },
            { icon: 'visibility', text: 'Watch bots compete in real-time with live event log' },
            { icon: 'emoji_events', text: 'Auction ends and winner is determined' },
          ].map((step, index) => (
            <li key={index} className="flex items-center gap-3">
              <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center text-sm font-medium">
                {index + 1}
              </span>
              <Icon name={step.icon} size="small" className="text-gray-400" />
              <span className="text-gray-700">{step.text}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Bot Strategies Reference */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Icon name="psychology" size="small" className="text-purple-600" />
          Bot Strategies
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          {[
            { name: 'Aggressive', icon: 'local_fire_department', color: 'red', desc: 'Bids immediately on every opportunity up to max budget' },
            { name: 'Conservative', icon: 'savings', color: 'green', desc: 'Hesitates, sometimes drops out early, careful with money' },
            { name: 'Sniper', icon: 'schedule', color: 'blue', desc: 'Waits until the last 30 seconds, then strikes fast' },
            { name: 'Random', icon: 'casino', color: 'purple', desc: '60% chance to bid each cycle, unpredictable behavior' },
          ].map((strategy) => (
            <div key={strategy.name} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-100">
              <Icon 
                name={strategy.icon} 
                className={`text-${strategy.color}-500`} 
              />
              <div>
                <p className="font-medium text-gray-900">{strategy.name}</p>
                <p className="text-sm text-gray-500">{strategy.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Demo Credentials */}
      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="account_circle" className="text-blue-600" />
          <div>
            <p className="font-medium text-blue-900">Manual Login (Optional)</p>
            <p className="text-sm text-blue-700 mt-1">
              Email: <code className="bg-blue-100 px-1.5 py-0.5 rounded">alice@demo.auction</code>,{' '}
              <code className="bg-blue-100 px-1.5 py-0.5 rounded">bob@demo.auction</code>,{' '}
              <code className="bg-blue-100 px-1.5 py-0.5 rounded">charlie@demo.auction</code>
            </p>
            <p className="text-sm text-blue-700">
              Password: <code className="bg-blue-100 px-1.5 py-0.5 rounded">demo123</code>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
